import type {Appareil} from "@mytour/shared";
import {
  ETAT_CONDUIT_OPTIONS,
  REMARQUES_MAX_LENGTH,
  TYPE_SUIES_OPTIONS,
  VOLUME_SUIES_OPTIONS,
  refineSuiesCoupling,
} from "@mytour/shared";
import {Splitter} from "@mytour/ui/layout";
import type {Control, FieldErrors, UseFormTrigger} from "react-hook-form";
import {Controller} from "react-hook-form";
import {FormFeedback, FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const inspectionSchema = z
  .object({
    controleVisuelAppareil: z.enum(["oui", "non"]),
    controleVisuelConduit: z.enum(["oui", "non", "partiel"]),
    controleVisuelSouche: z.enum(["oui", "non"]),
    tirage: z.enum(["conforme", "nonConforme", "nonVerifie"]),
    etatConduit: z.string().min(1, "Requis"),
    typeSuies: z.string().optional(),
    volumeSuies: z.string().optional(),
    aspirationSuies: z.boolean(),
    remarques: z
      .string()
      .max(REMARQUES_MAX_LENGTH, `${REMARQUES_MAX_LENGTH} caractères maximum`)
      .optional(),
    attestationVacuite: z.boolean(),
  })
  .superRefine(refineSuiesCoupling);

export type InspectionFormData = z.infer<typeof inspectionSchema>;

type Props = {
  control: Control<InspectionFormData>;
  errors: FieldErrors<InspectionFormData>;
  disabled: boolean;
  trigger: UseFormTrigger<InspectionFormData>;
};

export function InspectionForm({control, errors, disabled, trigger}: Props) {
  return (
    <>
      <h5 style={{marginBottom: "16px"}}>Contrôles et état</h5>

      <div style={{marginBottom: "8px"}}>
        <strong>Contrôles visuels</strong>
      </div>

      <RadioField
        name="controleVisuelAppareil"
        label="Appareil"
        options={[
          {value: "oui", label: "Oui"},
          {value: "non", label: "Non"},
        ]}
        control={control}
        disabled={disabled}
      />

      <RadioField
        name="controleVisuelConduit"
        label="Conduit"
        options={[
          {value: "oui", label: "Oui"},
          {value: "partiel", label: "Partiel"},
          {value: "non", label: "Non"},
        ]}
        control={control}
        disabled={disabled}
      />

      <RadioField
        name="controleVisuelSouche"
        label="Souche"
        options={[
          {value: "oui", label: "Oui"},
          {value: "non", label: "Non"},
        ]}
        control={control}
        disabled={disabled}
      />

      <RadioField
        name="tirage"
        label="Tirage"
        options={[
          {value: "conforme", label: "Conforme"},
          {value: "nonConforme", label: "Non conforme"},
          {value: "nonVerifie", label: "Non vérifié"},
        ]}
        control={control}
        disabled={disabled}
        column
      />

      <FormGroup floating style={{marginTop: "8px"}}>
        <Controller
          name="etatConduit"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-etatConduit"
              placeholder="État du conduit"
              invalid={!!errors.etatConduit}
              disabled={disabled}
            >
              <option value="">--</option>
              {ETAT_CONDUIT_OPTIONS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-etatConduit">État du conduit</Label>
      </FormGroup>

      <Splitter>
        <FormGroup floating>
          <Controller
            name="typeSuies"
            control={control}
            render={({field}) => (
              <Input
                {...field}
                onChange={handleSuiesFieldChange(field.onChange)}
                type="select"
                id="field-typeSuies"
                placeholder="Type de suies"
                invalid={!!errors.typeSuies}
                disabled={disabled}
              >
                <option value="">--</option>
                {TYPE_SUIES_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            )}
          />
          <Label for="field-typeSuies">Type de suies</Label>
          {errors.typeSuies && <FormFeedback>{errors.typeSuies.message}</FormFeedback>}
        </FormGroup>

        <FormGroup floating>
          <Controller
            name="volumeSuies"
            control={control}
            render={({field}) => (
              <Input
                {...field}
                onChange={handleSuiesFieldChange(field.onChange)}
                type="select"
                id="field-volumeSuies"
                placeholder="Volume de suies"
                invalid={!!errors.volumeSuies}
                disabled={disabled}
              >
                <option value="">--</option>
                {VOLUME_SUIES_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Input>
            )}
          />
          <Label for="field-volumeSuies">Volume de suies</Label>
          {errors.volumeSuies && <FormFeedback>{errors.volumeSuies.message}</FormFeedback>}
        </FormGroup>
      </Splitter>

      <FormGroup check>
        <Controller
          name="aspirationSuies"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="checkbox"
              id="field-aspirationSuies"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={disabled}
              value=""
            />
          )}
        />
        <Label check for="field-aspirationSuies">
          Aspiration des suies effectuée
        </Label>
      </FormGroup>

      <FormGroup floating style={{marginTop: "16px"}}>
        <Controller
          name="remarques"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="textarea"
              id="field-remarques"
              placeholder="Remarques"
              disabled={disabled}
              maxLength={REMARQUES_MAX_LENGTH}
              value={field.value ?? ""}
            />
          )}
        />
        <Label for="field-remarques">Remarques</Label>
      </FormGroup>

      <FormGroup check style={{marginTop: "8px"}}>
        <Controller
          name="attestationVacuite"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="checkbox"
              id="field-attestationVacuite"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={disabled}
              value=""
            />
          )}
        />
        <Label check for="field-attestationVacuite">
          Atteste de la vacuité du conduit après intervention
        </Label>
      </FormGroup>
    </>
  );

  /**
   * `trigger()` ne resterait pas sinon en phase avec l'écran : `formStep4` valide en
   * mode "onSubmit" (défaut RHF), donc corriger un select ne relance le resolver que
   * si on le redemande explicitement — sans ça, l'erreur de couplage reste affichée
   * sur les deux selects même une fois les deux champs renseignés, jusqu'au prochain
   * clic sur "Suite".
   */
  function handleSuiesFieldChange(fieldOnChange: (e: unknown) => void) {
    return (e: unknown) => {
      fieldOnChange(e);
      if (errors.typeSuies || errors.volumeSuies) {
        trigger(["typeSuies", "volumeSuies"]);
      }
    };
  }
}

function RadioField({
  name,
  label,
  options,
  control,
  disabled,
  column,
}: {
  name: string;
  label: string;
  options: {value: string; label: string}[];
  control: Control<any>;
  disabled: boolean;
  column?: boolean;
}) {
  return (
    <div style={{marginBottom: "8px"}}>
      <span style={{fontSize: "14px"}}>{label} : </span>
      <Controller
        name={name}
        control={control}
        render={({field}) => (
          <span
            style={{
              display: column ? "flex" : "inline-flex",
              flexDirection: column ? "column" : "row",
              gap: column ? "4px" : "12px",
              marginTop: column ? "4px" : undefined,
            }}
          >
            {options.map((opt) => (
              <FormGroup check inline={!column} key={opt.value}>
                <Input
                  type="radio"
                  id={`field-${name}-${opt.value}`}
                  checked={field.value === opt.value}
                  onChange={() => field.onChange(opt.value)}
                  disabled={disabled}
                />
                <Label check for={`field-${name}-${opt.value}`}>
                  {opt.label}
                </Label>
              </FormGroup>
            ))}
          </span>
        )}
      />
    </div>
  );
}

export function getInspectionDefaultValues(appareil: Appareil): InspectionFormData {
  return {
    controleVisuelAppareil: appareil.controleVisuelAppareil,
    controleVisuelConduit: appareil.controleVisuelConduit,
    controleVisuelSouche: appareil.controleVisuelSouche,
    tirage: appareil.tirage,
    etatConduit: appareil.etatConduit,
    typeSuies: appareil.typeSuies ?? "",
    volumeSuies: appareil.volumeSuies ?? "",
    aspirationSuies: appareil.aspirationSuies,
    remarques: appareil.remarques ?? "",
    attestationVacuite: appareil.attestationVacuite,
  };
}
